(() => {
  "use strict";

  const THEME_KEY = "carplay-lyon:theme";
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  const readTheme = () => {
    try {
      const value = localStorage.getItem(THEME_KEY);
      return value === "light" || value === "dark" ? value : "system";
    } catch {
      return "system";
    }
  };

  const applyTheme = (value) => {
    if (value === "system") document.documentElement.removeAttribute("data-theme");
    else document.documentElement.setAttribute("data-theme", value);
    document.querySelectorAll("[data-theme-choice]").forEach((button) => {
      button.setAttribute("aria-checked", String(button.dataset.themeChoice === value));
    });
  };

  const initTheme = () => {
    document.querySelectorAll("[data-theme-choice]").forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = "true";
      button.addEventListener("click", () => {
        const value = button.dataset.themeChoice;
        applyTheme(value);
        try {
          if (value === "system") localStorage.removeItem(THEME_KEY);
          else localStorage.setItem(THEME_KEY, value);
        } catch {
          // The visual choice still applies for this page.
        }
      });
    });
    applyTheme(readTheme());
  };

  const initNavigation = () => {
    const header = document.querySelector("[data-header]");
    const toggle = document.querySelector("[data-nav-toggle]");
    const panel = document.querySelector("[data-nav-panel]");

    if (header && !header.dataset.scrollBound) {
      header.dataset.scrollBound = "true";
      const update = () => header.classList.toggle("is-scrolled", window.scrollY > 8);
      update();
      window.addEventListener("scroll", update, { passive: true });
    }

    if (!toggle || !panel || toggle.dataset.bound) return;
    toggle.dataset.bound = "true";

    const setOpen = (open, moveFocus = true) => {
      toggle.setAttribute("aria-expanded", String(open));
      toggle.setAttribute("aria-label", open ? "Fermer le menu" : "Ouvrir le menu");
      panel.classList.toggle("is-open", open);
      panel.setAttribute("aria-hidden", String(!open));
      panel.inert = !open;
      document.body.style.overflow = open ? "hidden" : "";
      if (moveFocus) {
        if (open) panel.querySelector("a")?.focus();
        else toggle.focus();
      }
    };

    setOpen(false, false);
    toggle.addEventListener("click", () => {
      setOpen(toggle.getAttribute("aria-expanded") !== "true");
    });
    panel.addEventListener("click", (event) => {
      if (event.target.closest("a")) setOpen(false, false);
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && toggle.getAttribute("aria-expanded") === "true") {
        setOpen(false);
      }
      if (event.key === "Tab" && toggle.getAttribute("aria-expanded") === "true") {
        const focusable = [toggle, ...panel.querySelectorAll('a[href], button, input')];
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    });
    matchMedia('(min-width: 1024px)').addEventListener('change', event => {
      if (event.matches) setOpen(false, false);
    });
  };

  const initMobileAction = () => {
    const bar = document.querySelector('[data-mobile-cta]');
    const action = document.querySelector('.hero-copy .btn-primary');
    if (!bar || !action || bar.dataset.bound || !('IntersectionObserver' in window)) return;
    bar.dataset.bound = 'true';
    new IntersectionObserver(([entry]) => {
      bar.hidden = entry.isIntersecting || entry.boundingClientRect.bottom > (entry.rootBounds?.top ?? 64);
    }, { threshold: 0, rootMargin: '-64px 0px 0px 0px' }).observe(action);
  };

  const initAccordions = () => {
    document.querySelectorAll("[data-accordion]").forEach((list) => {
      if (list.dataset.bound) return;
      list.dataset.bound = "true";
      list.addEventListener("click", (event) => {
        const button = event.target.closest("button[aria-controls]");
        if (!button) return;

        const nextOpen = button.getAttribute("aria-expanded") !== "true";
        list.querySelectorAll("button[aria-controls]").forEach((other) => {
          const open = other === button && nextOpen;
          other.setAttribute("aria-expanded", String(open));
          const panel = document.getElementById(other.getAttribute("aria-controls"));
          if (!panel) return;
          panel.classList.toggle("grid-rows-[1fr]", open);
          panel.classList.toggle("grid-rows-[0fr]", !open);
          panel.classList.toggle("opacity-100", open);
          panel.classList.toggle("opacity-0", !open);
        });
      });
    });
  };

  /*
   * Références cherchées sans résultat dans le parcours rapide.
   *
   * Elles sont écrites par le configurateur dans le navigateur du visiteur, et
   * ne quittent jamais son appareil de leur propre chef. Les reprendre ici, et
   * seulement ici, leur donne leur seule utilité honnête : quand le visiteur
   * décide de nous écrire, il n'a pas à retaper de mémoire la référence que
   * nous n'avons pas su lire — et nous apprenons ce qui manque à la base.
   *
   * Le champ reste entièrement modifiable, et n'est rempli que s'il est vide.
   */
  const initUnmatchedSearches = () => {
    const field = document.querySelector("[data-contact-body]");
    if (!field || field.value.trim()) return;
    let searches = [];
    try {
      searches = JSON.parse(localStorage.getItem("carplay-lyon:unmatched:v1") || "[]");
    } catch {
      return;
    }
    if (!searches.length) return;
    const references = searches.map((entry) => entry.q).slice(-5);
    field.value = `J'ai cherché ${references.length > 1 ? "ces références" : "cette référence"} sur votre site sans obtenir de résultat :\n` +
      references.map((reference) => `  · ${reference}`).join("\n") +
      "\n\n";
    // Le curseur se place à la fin, pour que le visiteur enchaîne sa question.
    field.setSelectionRange(field.value.length, field.value.length);
  };

  const initVehicleFields = () => {
    document.querySelectorAll("[data-vehicle-field]").forEach((field) => {
      if (field.value) return;
      try {
        const vehicle = JSON.parse(localStorage.getItem("carplay-lyon:vehicle:v1") || "null");
        if (!vehicle) return;
        const value = vehicle.label || [
          vehicle.brandSlug,
          vehicle.generationSlug,
          vehicle.year,
          vehicle.systemSlug,
        ].filter(Boolean).join(" · ");
        if (value) field.value = value;
      } catch {
        // The field remains editable when storage is unavailable.
      }
    });
  };

  const normalize = (value) => String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, " ")
    .trim();

  const initFilters = () => {
    document.querySelectorAll("[data-filtre]").forEach((root) => {
      if (root.dataset.bound) return;
      const name = root.dataset.filtre;
      const containers = document.querySelectorAll(`[data-filtre-cible="${name}"]`);
      const input = root.querySelector("[data-filtre-champ]");
      const clear = root.querySelector("[data-filtre-effacer]");
      if (!containers.length || !input || !clear) return;

      root.dataset.bound = "true";
      const items = [];
      containers.forEach((container) => {
        container.querySelectorAll("[data-filtre-item]").forEach((item) => {
          items.push({ item, text: normalize(item.textContent) });
        });
      });
      const count = document.querySelector(`[data-filtre-compte="${name}"]`);
      const noun = root.dataset.filtreNom || "résultat";

      const update = () => {
        const words = normalize(input.value).split(" ").filter(Boolean);
        let visible = 0;
        items.forEach(({ item, text }) => {
          const match = words.every((word) => text.includes(word));
          item.hidden = !match;
          if (match) visible += 1;
        });
        containers.forEach((container) => {
          container.querySelectorAll("[data-filtre-groupe]").forEach((group) => {
            group.hidden = ![...group.querySelectorAll("[data-filtre-item]")]
              .some((item) => !item.hidden);
          });
        });
        clear.hidden = !words.length;
        if (count) {
          count.textContent = !words.length
            ? ""
            : visible
              ? `${visible} ${noun}${visible > 1 ? "s" : ""} sur ${items.length}.`
              : `Aucun ${noun} pour « ${input.value.trim()} ».`;
        }
      };

      input.addEventListener("input", update);
      input.addEventListener("keydown", (event) => {
        if (event.key !== "Escape" || !input.value) return;
        event.preventDefault();
        input.value = "";
        update();
      });
      clear.addEventListener("click", () => {
        input.value = "";
        update();
        input.focus();
      });
      root.hidden = false;
      update();
    });
  };

  const initCarousels = () => {
    document.querySelectorAll("[data-carousel]").forEach((root) => {
      if (root.dataset.bound) return;
      const track = root.querySelector("[data-carousel-track]");
      const dots = root.querySelector("[data-carousel-dots]");
      if (!track || !dots || track.children.length < 2) return;

      root.dataset.bound = "true";
      const slides = [...track.children];
      const controls = root.parentElement.querySelector("[data-carousel-arrows]")
        || root.closest("section")?.querySelector("[data-carousel-arrows]");
      const previous = root.closest("section")?.querySelector("[data-carousel-prev]");
      const next = root.closest("section")?.querySelector("[data-carousel-next]");
      if (controls) controls.hidden = false;

      const behavior = reducedMotion.matches ? "auto" : "smooth";
      const goTo = (index) => slides[index]?.scrollIntoView({
        behavior,
        block: "nearest",
        inline: "center",
      });
      if (previous) previous.addEventListener("click", () => {
        const current = Number(dots.querySelector("[aria-selected=true]")?.dataset.index || 0);
        goTo(Math.max(0, current - 1));
      });
      if (next) next.addEventListener("click", () => {
        const current = Number(dots.querySelector("[aria-selected=true]")?.dataset.index || 0);
        goTo(Math.min(slides.length - 1, current + 1));
      });

      dots.hidden = false;
      dots.innerHTML = slides.map((_, index) => (
        `<button type="button" role="tab" data-index="${index}" aria-label="Image ${index + 1}" class="h-1.5 w-5 cursor-pointer rounded-full bg-line/60"></button>`
      )).join("");
      const buttons = [...dots.children];
      dots.addEventListener("click", (event) => {
        const button = event.target.closest("[data-index]");
        if (button) goTo(Number(button.dataset.index));
      });

      const mark = (index) => {
        buttons.forEach((button, buttonIndex) => {
          const active = index === buttonIndex;
          button.setAttribute("aria-selected", String(active));
          button.classList.toggle("bg-iris-500", active);
          button.classList.toggle("w-8", active);
          button.classList.toggle("bg-line/60", !active);
          button.classList.toggle("w-5", !active);
        });
        if (previous) previous.disabled = index === 0;
        if (next) next.disabled = index === slides.length - 1;
      };

      const observer = new IntersectionObserver((entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) mark(slides.indexOf(visible.target));
      }, { root: track, threshold: [0.55, 0.8] });
      slides.forEach((slide) => observer.observe(slide));
      mark(0);
    });
  };

  const start = () => {
    initTheme();
    initNavigation();
    initAccordions();
    initVehicleFields();
    initUnmatchedSearches();
    initFilters();
    initCarousels();
    initMobileAction();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
  window.addEventListener("storage", (event) => {
    if (event.key === THEME_KEY) applyTheme(readTheme());
  });
  document.addEventListener("shopify:section:load", start);
})();
